import { type FC, type FormEvent, useState } from "react";
import { Link, useNavigate } from "react-router";
import { normalizeOrderCode } from "../../../libs/mypage/orderCode";
import { Button } from "../components/Button";
import { Container } from "../components/Container";
import { Field } from "../components/Field";
import { FieldSelect } from "../components/FieldSelect";
import type { OrderDetailDTO } from "../components/format";
import { LinkButton } from "../components/LinkButton";
import { Notice } from "../components/Notice";
import { useNotifier } from "../components/Notifier";
import { PageHeading } from "../components/PageHeading";
import { SectionHeading } from "../components/SectionHeading";
import { useDecodedOrderCode } from "../hooks/useDecodedOrderCode";
import { useDirtyGuard } from "../hooks/useDirtyGuard";
import { useOrder } from "../hooks/useOrder";
import { apiClient } from "../rpc";

const meAddress = apiClient.me.orders[":code"].address;

const PREFECTURES = [
  "北海道",
  "青森県",
  "岩手県",
  "宮城県",
  "秋田県",
  "山形県",
  "福島県",
  "茨城県",
  "栃木県",
  "群馬県",
  "埼玉県",
  "千葉県",
  "東京都",
  "神奈川県",
  "新潟県",
  "富山県",
  "石川県",
  "福井県",
  "山梨県",
  "長野県",
  "岐阜県",
  "静岡県",
  "愛知県",
  "三重県",
  "滋賀県",
  "京都府",
  "大阪府",
  "兵庫県",
  "奈良県",
  "和歌山県",
  "鳥取県",
  "島根県",
  "岡山県",
  "広島県",
  "山口県",
  "徳島県",
  "香川県",
  "愛媛県",
  "高知県",
  "福岡県",
  "佐賀県",
  "長崎県",
  "熊本県",
  "大分県",
  "宮崎県",
  "鹿児島県",
  "沖縄県",
] as const;

type SubmitState =
  | { status: "idle" }
  | { status: "submitting" }
  | { status: "error"; message: string };

export const AddressFormPage: FC = () => {
  const code = useDecodedOrderCode();
  const result = useOrder(code);

  if (result.status === "loading") {
    return (
      <Container>
        <p className="py-8 text-center text-neutral-500 text-sm">読み込み中…</p>
      </Container>
    );
  }
  if (result.status === "not-found") {
    return (
      <Container>
        <PageHeading title="ご注文が見つかりませんでした" />
        <Link to="/dashboard" className="text-sm underline underline-offset-4">
          注文履歴に戻る
        </Link>
      </Container>
    );
  }
  if (result.status === "error") {
    return (
      <Container>
        <PageHeading title="注文情報を取得できませんでした" description={result.message} />
        <Link to="/dashboard" className="text-sm underline underline-offset-4">
          注文履歴に戻る
        </Link>
      </Container>
    );
  }

  const order = result.order;
  if (!order.isAddressEditable) {
    return (
      <Container>
        <p className="mb-4 text-neutral-500 text-xs">注文番号 {order.code}</p>
        <PageHeading
          title="この注文のお届け先は変更できません"
          description="出荷作業に入っている、または既にキャンセル/出荷済みのご注文です。詳細はサポートまでお問い合わせください。"
        />
        <Link
          to={`/orders/${normalizeOrderCode(order.code)}`}
          className="text-sm underline underline-offset-4"
        >
          注文詳細に戻る
        </Link>
      </Container>
    );
  }

  return <AddressFormBody order={order} />;
};

const isValidPrefecture = (v: string): v is (typeof PREFECTURES)[number] =>
  (PREFECTURES as readonly string[]).includes(v);

const AddressFormBody: FC<{ order: OrderDetailDTO }> = ({ order }) => {
  const navigate = useNavigate();
  const { notify } = useNotifier();
  const slug = normalizeOrderCode(order.code);
  const addr = order.shippingAddress;

  const [lastName, setLastName] = useState(addr?.lastName ?? "");
  const [firstName, setFirstName] = useState(addr?.firstName ?? "");
  const [postalCode, setPostalCode] = useState(addr?.postalCode ?? "");
  const [prefecture, setPrefecture] = useState<string>(addr?.prefecture ?? "東京都");
  const [city, setCity] = useState(addr?.city ?? "");
  const [address1, setAddress1] = useState(addr?.address1 ?? "");
  const [address2, setAddress2] = useState(addr?.address2 ?? "");
  const [phone, setPhone] = useState(addr?.phone ?? "");
  const [state, setState] = useState<SubmitState>({ status: "idle" });

  // pre-fill 値から 1 つでも動いていれば変更あり。送信ボタンの活性と離脱インターセプトの
  // 両方がこれを見る (同じ内容を送っても Logiless / Shopify は更新され通知メールも飛ぶので、
  // 「直したのに反映されない」と感じた顧客が同じ内容を送り直せる状態を作らない)。
  const hasChanges =
    lastName !== (addr?.lastName ?? "") ||
    firstName !== (addr?.firstName ?? "") ||
    postalCode !== (addr?.postalCode ?? "") ||
    prefecture !== (addr?.prefecture ?? "東京都") ||
    city !== (addr?.city ?? "") ||
    address1 !== (addr?.address1 ?? "") ||
    address2 !== (addr?.address2 ?? "") ||
    phone !== (addr?.phone ?? "");

  // 離脱インターセプト: submit 中は自分で navigate するので block を解除する
  // (submit 成功時の navigate は submitting 入りで gate されて通る)。
  useDirtyGuard(state.status !== "submitting" && hasChanges);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!hasChanges) return;

    const trimmedLast = lastName.trim();
    const trimmedFirst = firstName.trim();
    const normalizedPostal = postalCode.replace(/[^0-9]/g, "");
    const normalizedPhone = phone.replace(/[^0-9]/g, "");
    const trimmedCity = city.trim();
    const trimmedAddress1 = address1.trim();
    const trimmedAddress2 = address2.trim();

    if (!trimmedLast) return setState({ status: "error", message: "姓を入力してください" });
    if (!trimmedFirst) return setState({ status: "error", message: "名を入力してください" });
    if (!/^\d{7}$/.test(normalizedPostal))
      return setState({ status: "error", message: "郵便番号は 7 桁の数字で入力してください" });
    if (!isValidPrefecture(prefecture))
      return setState({ status: "error", message: "都道府県を選択してください" });
    if (!trimmedCity) return setState({ status: "error", message: "市区町村を入力してください" });
    if (!trimmedAddress1)
      return setState({ status: "error", message: "町名・番地を入力してください" });
    if (!/^\d{10,13}$/.test(normalizedPhone))
      return setState({ status: "error", message: "電話番号は 10〜13 桁の数字で入力してください" });

    setState({ status: "submitting" });
    try {
      const res = await meAddress.$post({
        param: { code: slug },
        json: {
          lastName: trimmedLast,
          firstName: trimmedFirst,
          postalCode: normalizedPostal,
          prefecture,
          city: trimmedCity,
          address1: trimmedAddress1,
          address2: trimmedAddress2 === "" ? null : trimmedAddress2,
          phone: normalizedPhone,
        },
      });
      if (res.status === 200) {
        notify({ kind: "success", message: "お届け先を変更しました。" });
        navigate(`/orders/${slug}`, { replace: true });
        return;
      }
      // 5xx (サーバ/連携失敗) は生メッセージ ("Internal Server Error" 等) を出さず定型文にする。
      // 4xx (変更不可等) はサーバの案内文をそのまま出す (ユーザが対処できる内容のため)。
      const data = await res.json().catch(() => ({}));
      const message =
        res.status >= 500
          ? "お届け先の変更に失敗しました。時間をおいて再度お試しください。改善しない場合は c-support@survaq.com までご連絡ください。"
          : "message" in data && typeof data.message === "string"
            ? data.message
            : `お届け先の変更に失敗しました (HTTP ${res.status})`;
      setState({ status: "error", message });
      notify({ kind: "error", message });
    } catch {
      // 通信断など。生の Error.message は出さず定型文にする。
      const message = "通信に失敗しました。電波の良い場所で少し時間をおいて再度お試しください。";
      setState({ status: "error", message });
      notify({ kind: "error", message });
    }
  };

  const submitting = state.status === "submitting";

  return (
    <Container>
      <Link
        to={`/orders/${slug}`}
        className="mb-6 inline-block text-neutral-500 text-xs hover:text-neutral-900"
      >
        ← 注文詳細
      </Link>
      <PageHeading
        title="お届け先を変更"
        description="出荷作業に入る前であればお届け先を変更できます。"
      />

      {order.hasAddressDeficiency && (
        <Notice variant="warn" title="お届け先のご住所をご確認ください" className="mb-6">
          ご登録のお届け先のご住所を確認できませんでした。 （郵便番号とご住所が一致しない、
          または番地が不足している可能性がございます。）
          郵便番号・都道府県・市区町村・「町名・番地」が正しく、
          番地まで入力されているかご確認のうえ、 保存してください。
        </Notice>
      )}

      <SectionHeading title="お届け先" />
      <form onSubmit={handleSubmit} className="space-y-4 py-4">
        <div className="grid grid-cols-2 gap-3">
          <Field
            label="姓 (必須)"
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.currentTarget.value)}
            required
            maxLength={25}
            placeholder="例: 山田"
          />
          <Field
            label="名 (必須)"
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.currentTarget.value)}
            required
            maxLength={25}
            placeholder="例: 太郎"
          />
        </div>
        <Field
          label="郵便番号 (必須)"
          type="text"
          inputMode="numeric"
          value={postalCode}
          onChange={(e) => setPostalCode(e.currentTarget.value)}
          required
          maxLength={8}
          hint="ハイフン無しの 7 桁数字"
        />
        <FieldSelect
          label="都道府県 (必須)"
          value={prefecture}
          onChange={(e) => setPrefecture(e.currentTarget.value)}
          required
          options={PREFECTURES}
        />
        <Field
          label="市区町村 (必須)"
          type="text"
          value={city}
          onChange={(e) => setCity(e.currentTarget.value)}
          required
          maxLength={50}
          hint="例: 渋谷区"
        />
        <Field
          label="町名・番地 (必須)"
          type="text"
          value={address1}
          onChange={(e) => setAddress1(e.currentTarget.value)}
          required
          maxLength={100}
          hint={
            order.hasAddressDeficiency
              ? "番地まで正しくご入力ください (例: 神宮前 1-2-3)"
              : "例: 神宮前 1-2-3"
          }
        />
        <Field
          label="建物名・部屋番号"
          type="text"
          value={address2}
          onChange={(e) => setAddress2(e.currentTarget.value)}
          maxLength={100}
          hint="任意。マンション名や号室など"
        />
        <Field
          label="電話番号 (必須)"
          type="tel"
          inputMode="numeric"
          value={phone}
          onChange={(e) => setPhone(e.currentTarget.value)}
          required
          maxLength={13}
          hint="ハイフン無しの数字のみ"
        />

        {state.status === "error" && <p className="text-red-600 text-xs">{state.message}</p>}

        <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:justify-end">
          <LinkButton to={`/orders/${slug}`} variant="outline">
            戻る
          </LinkButton>
          <Button type="submit" loading={submitting} disabled={!hasChanges}>
            {submitting ? "保存中…" : "この内容で変更する"}
          </Button>
        </div>
      </form>
    </Container>
  );
};
